import * as React from "react";

import {
  IFluid,
  ILocalFile
} from "src/types/local-files";

import { IImageMeta } from "../../types/local-files";
import styled from "../../theme/styled";
import { fontLineHeightHelper } from "../../theme/theme-helper";
import {
  ColumnWidthWrapper,
  SmallColumnWidthWrapper
} from "../confines-wrappers";
import ParagraphBoxOutImageColumnWidth from "./paragraph-box-out-image-column-width";
import ParagraphBoxOutImageHalfColumnWidth from "./paragraph-box-out-image-half-column-width";
import ParagraphBoxOutSideBySideImagesColumn from "./paragraph-box-out-side-by-side-images-column";
import ParagraphBoxOutStackedImages from "./paragraph-box-out-stacked-images";
import ParagraphBoxOutTestimonials, { IParagraphTestimonial } from "./paragraph-box-out-testimonials";
import ParagraphBoxOutText from "./paragraph-box-out-text";
import ParagraphBoxOutVideoColumnWidth from "./paragraph-box-out-video-column-width";
import ParagraphBoxOutVideoHalfColumnWidth from "./paragraph-box-out-video-half-column-width";
import ParagraphCodeSnippet from "./paragraph-code-snippet";
import ParagraphImageColumnWidth from "./paragraph-image-column-width";
import ParagraphImageFullWidth from "./paragraph-image-full-width";
import ParagraphImagesSideBySideColumn from "./paragraph-images-side-by-side-column";
import ParagraphVideoColumnWidth from "./paragraph-video-column-width";
import ParagraphVideoHalfColumnWidth from "./paragraph-video-half-column-width";
import ParagraphWsywig from "./paragraph-wsywig";
import ReadTime from "./read-time";
import Summary from "./summary";

export interface IFieldTechnicalArticle {
  __typename: string;
  field_copy?: {
    processed: string;
  };
  field_code_snippet?: string;
  field_code_snippet_type?: string | null;
  field_background_colour_s_?: string[];
  field_boxout_header?: string;
  field_boxout_link?: {
    uri?: string;
    title?: string;
  };
  field_boxout_text?: {
    processed: string;
  };
  field_video_url?: string;
  field_video_caption?: string;
  field_video_caption_colour?: string;
  field_video_autoplay?: boolean;
  field_image: IImageMeta;
  field_images: IImageMeta[];
  relationships?: {
    field_images?: Array<ILocalFile<IFluid>>;
    field_image?: ILocalFile<IFluid>;
    field_testimonial_item?: IParagraphTestimonial[];
  };
}

export type IFieldTechnicalArticleColumnType = "basic" | "project";

export const DynamicWrapper = (props: {
  type: IFieldTechnicalArticleColumnType;
  children: JSX.Element;
}) => {
  switch (props.type) {
    default:
    case "basic":
      return (
        <SmallColumnWidthWrapper>
          {props.children}
        </SmallColumnWidthWrapper>
      );

    case "project":
      return (
        <ColumnWidthWrapper>
          {props.children}
        </ColumnWidthWrapper>
      );
  }
};

interface IProps {
  type: IFieldTechnicalArticleColumnType;
  readTime?: string;
  summary?: string;
  paragraphs: IFieldTechnicalArticle[];
}

interface IArticleWrapper {
  type: IFieldTechnicalArticleColumnType;
}

const ArticleWrapper = styled<IArticleWrapper, "div">("div")`
  [class*="paragraph-"] {
    transition: 0.2s ease-in-out;
    padding-top: ${props => (props.type === "basic" ? "8%" : "10%")};
  }

  .paragraph-wysiwyg + .paragraph-wysiwyg {
    padding-top: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "default"
    })};
  }

  [class*="paragraph-full-width-image"] + [class*="paragraph-full-width-image"],
  [class*="paragraph-full-width-image"] + [class*="paragraph-box-out"],
  [class*="paragraph-box-out"] + [class*="paragraph-box-out"],
  [class*="paragraph-box-out"] + [class*="paragraph-full-width-image"] {
    padding-top: 0;
  }

  [class*="paragraph-box-out"],
  .paragraph-full-width-image {
    &:first-child {
      padding-top: 0;
    }
  }

  .paragraph-wysiwyg,
  .paragraph-code-snippet,
  .paragraph-image-column-width,
  .paragraph-side-by-side-image,
  .paragraph-video-column-width {
    &:last-child {
      padding-bottom: ${props => (props.type === "basic" ? "8%" : "10%")};
    }
  }

  @media (min-width: 600px) {
    [class*="paragraph-"] {
      padding-top: ${props => (props.type === "basic" ? "3.8rem" : "6.4rem")};
    }

    .paragraph-wysiwyg,
    .paragraph-code-snippet,
    .paragraph-image-column-width,
    .paragraph-side-by-side-image,
    .paragraph-video-column-width {
      &:last-child {
        padding-bottom: ${props =>
    props.type === "basic" ? "3.8rem" : "6.4rem"};
      }
    }
  }

  @media (min-width: 960px) {
    [class*="paragraph-"] {
      padding-top: 9.6rem;
      padding-top: ${props => (props.type === "basic" ? "5.4rem" : "9.6rem")};
    }

    .paragraph-wysiwyg,
    .paragraph-code-snippet,
    .paragraph-image-column-width,
    .paragraph-side-by-side-image,
    .paragraph-video-column-width {
      &:last-child {
        padding-bottom: ${props =>
    props.type === "basic" ? "5.4rem" : "9.6rem"};
      }
    }
  }
`;

const FieldTechnicalArticle = ({
  type,
  readTime,
  summary,
  paragraphs
}: IProps) => {
  return (
    <ArticleWrapper type={type}>
      <ReadTime
        type={type}
        readTime={readTime}
      />

      {summary && (
        <aside>
          <Summary
            type={type}
            content={summary}
          />
        </aside>
      )}

      {paragraphs ?
        paragraphs.map((paragraph: IFieldTechnicalArticle, i: number) => {
          switch (paragraph.__typename) {
            case "paragraph__boxout_side_by_side_images_colum":
              return (
                <ParagraphBoxOutSideBySideImagesColumn
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__boxout_image_column_width_":
              return (
                <ParagraphBoxOutImageColumnWidth
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__boxout_image_half_column_width_":
              return (
                <ParagraphBoxOutImageHalfColumnWidth
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__boxout_plain_text":
              return (
                <ParagraphBoxOutText
                  key={i}
                  type={type}
                  content={paragraph}
                />
              );
            case "paragraph__boxout_stacked_images":
              return (
                <ParagraphBoxOutStackedImages
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__boxout_video_column_width_":
              return (
                <ParagraphBoxOutVideoColumnWidth
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__boxout_video_half_column_width_":
              return (
                <ParagraphBoxOutVideoHalfColumnWidth
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__testimonial_s_":
              return (
                <ParagraphBoxOutTestimonials
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__wysiwyg":
              return (
                <ParagraphWsywig
                  key={i}
                  type={type}
                  content={paragraph}
                />
              );
            case "paragraph__images_side_by_side_column_width":
              return (
                <ParagraphImagesSideBySideColumn
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__code_snippet":
              return (
                <ParagraphCodeSnippet
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__image_column_width_":
              return (
                <ParagraphImageColumnWidth
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__image_full_width_":
              return (
                <ParagraphImageFullWidth
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__video_column_width_":
              return (
                <ParagraphVideoColumnWidth
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            case "paragraph__video_hacolumn_width_":
              return (
                <ParagraphVideoHalfColumnWidth
                  type={type}
                  content={paragraph}
                  key={i}
                />
              );
            default:
              return null;
          }
        }) :
        "No content - Oh Noes!"}
    </ArticleWrapper>
  );
};

export default FieldTechnicalArticle;
